import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FieldArray, getIn, useFormikContext } from "formik";
import isEqual from "react-fast-compare";
import { Link } from "react-router-dom";
import { IconPlus, IconTrash } from "@tabler/icons-react";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import {
  formatBalanceAffectation,
  getBalanceAffectation,
  getBalanceParentId,
} from "../../utils/balances";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import {
  BalanceDomain,
  DOMAINE_ID_SEPARATOR,
} from "../../utils/constants/balances";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import Button from "../UI/Button";
// @ts-expect-error pure js
import { balanceIV } from "../../models/balanceTenant";
import Text from "../UI/Text";
import {
  buildPropertyFoldersTree,
  findPropertyFolder,
  // @ts-expect-error pure js
} from "../../utils/properties";
import { PropertyStatus } from "../../models/property";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import useRepartitionKeys from "../../hooks/use-repartition-keys";
// @ts-expect-error pure js
import AffectationSelect from "../UI/AffectationSelect";
import MeterSplitPanel from "./MeterSplitPanel";
import type { Balance } from "../../models/balance";
import {
  RepartitionKeyUnit,
  repartitionKeyAllocatedShares,
  repartitionKeyBase,
} from "../../models/financeConfig";
import type {
  RepartitionKey,
  RepartitionKeyEntry,
} from "../../models/financeConfig";
import styles from "./BalanceSplitFields.module.css";
import Callout from "../UI/Callout";
import IconButton from "../UI/IconButton";

const REPARTITION_TYPE_OPTIONS = [
  { value: "prorata_surface", label: "Prorata surface" },
  { value: "uniform", label: "Égale" },
  { value: "manual", label: "Manuelle" },
];

const METER_REPARTITION_TYPE_OPTION = { value: "meter", label: "Par compteur" };

class RepartitionType {
  static PRORATA_SURFACE = "prorata_surface";
  static UNIFORM = "uniform";
  static MANUAL = "manual";
  static METER = "meter";
}

interface BalanceSplitFieldsProps {
  name: string;
  parentId: number;
  domaineBalance: string;
  /** Ignoré quand `isFinanceConfig` : la répartition est alors en % (base 10000). */
  totalAmountCents?: number;
  childBalances?: Balance[];
  isFinanceConfig?: boolean;
  /** Avec `isFinanceConfig` : en tantièmes, saisie libre répartie au prorata de la somme. */
  repartitionUnit?: RepartitionKeyUnit;
  /** Total des tantièmes saisi sur la clé ; 0 = somme des lots. */
  totalShares?: number;
  hasPeriod?: boolean;
  periodeDebut?: string;
  periodeFin?: string;
}

export default function BalanceSplitFields({
  name,
  parentId,
  domaineBalance,
  totalAmountCents = 0,
  childBalances,
  isFinanceConfig,
  repartitionUnit = RepartitionKeyUnit.PERCENT,
  totalShares = 0,
  hasPeriod,
  periodeDebut,
  periodeFin,
}: BalanceSplitFieldsProps) {
  const {
    properties,
    propertiesById,
    isLoading: isLoadingProperties,
    error: errorProperties,
  } = useProperties({ withById: true });
  const {
    propertyFolders,
    propertyFoldersById,
    isLoading: isLoadingFolders,
    error: errorFolders,
  } = usePropertyFolders({
    withById: true,
  });
  if (isFinanceConfig) totalAmountCents = 10000;
  const isShare =
    !!isFinanceConfig && repartitionUnit === RepartitionKeyUnit.SHARE;

  const { values, setFieldValue, handleChange } = useFormikContext<{
    id_transaction?: number;
  }>();
  const [selectedRepartitionType, setRepartitionType] = useState(
    childBalances ? RepartitionType.MANUAL : RepartitionType.PRORATA_SURFACE,
  );
  const repartitionType = isShare
    ? RepartitionType.MANUAL
    : selectedRepartitionType;
  const [repartitionWarning, setRepartitionWarning] = useState("");
  const [selectedRepartitionKey, setSelectedRepartitionKey] =
    useState<RepartitionKey | null>(null);

  const { repartitionKeys } = useRepartitionKeys(
    domaineBalance === BalanceDomain.FOLDER && parentId ? parentId : null,
  );

  const folderTree = useMemo(() => {
    return buildPropertyFoldersTree(propertyFolders, properties);
  }, [properties, propertyFolders]);
  const parentFolder = useMemo(() => {
    return findPropertyFolder(folderTree.property_folders, parentId);
  }, [folderTree, parentId]);

  const optionsNb =
    parentFolder.properties.length + parentFolder.property_folders.length;
  const prevParentFolder = useRef<unknown>(null);
  const correlationId = useRef<string>(crypto.randomUUID());

  const balanceAffectations = getIn(values, name, []).map((balance: Balance) =>
    formatBalanceAffectation(balance.domaine, getBalanceParentId(balance)),
  );
  const balanceAffectationsCSV = balanceAffectations.join();

  useEffect(() => {
    function getInitialSplitValues() {
      if (childBalances && childBalances.length > 0) {
        correlationId.current = childBalances[0].correlation_id ?? "";
        return childBalances.map((balance) => ({
          ...balance,
          affectation: getBalanceAffectation(balance),
        }));
      }

      const splitInitialValues = [];
      for (const prop of parentFolder.properties) {
        if (prop.status === PropertyStatus.Archive.status) continue;
        splitInitialValues.push({
          ...balanceIV,
          id_bien: prop.id,
          domaine: BalanceDomain.PROPERTY,
          affectation: formatBalanceAffectation(
            BalanceDomain.PROPERTY,
            prop.id,
          ),
          id_transaction: values.id_transaction,
          correlation_id: correlationId.current,
        });
      }
      for (const folder of parentFolder.property_folders) {
        if (folder.status === PropertyStatus.Archive.status) continue;
        splitInitialValues.push({
          ...balanceIV,
          id_property_folder: folder.id,
          domaine: BalanceDomain.FOLDER,
          affectation: formatBalanceAffectation(
            BalanceDomain.FOLDER,
            folder.id,
          ),
          correlation_id: correlationId.current,
        });
      }
      return splitInitialValues;
    }

    (async () => {
      let affectations = balanceAffectationsCSV.split(",");

      if (!isEqual(prevParentFolder.current, parentFolder)) {
        const splitInitialValues = getInitialSplitValues();
        await setFieldValue(name, splitInitialValues);
        affectations = splitInitialValues.map((balance) => balance.affectation);
        prevParentFolder.current = parentFolder;
        setSelectedRepartitionKey(null);
      }

      let amountsCents: (number | null)[] = [];
      // Une clé en tantièmes dont des lots sont sortis ne couvre qu'une partie du montant.
      let expectedTotalCents = totalAmountCents;
      const selectedPropertiesAndFolders: [
        { surface_total?: number } | null,
        string | null,
      ][] = [];
      affectations.forEach((affectation: string) => {
        const [domaine, parentId] = affectation.split(DOMAINE_ID_SEPARATOR);
        let collection;
        if (domaine === BalanceDomain.PROPERTY) {
          collection = propertiesById;
        } else if (domaine === BalanceDomain.FOLDER) {
          collection = propertyFoldersById;
        }

        if (collection) {
          selectedPropertiesAndFolders.push([collection[parentId], domaine]);
        } else {
          selectedPropertiesAndFolders.push([null, null]);
        }
      });

      if (repartitionType === RepartitionType.PRORATA_SURFACE) {
        const totalSurface = selectedPropertiesAndFolders.reduce(
          (acc, [propOrFolder]) => acc + (propOrFolder?.surface_total ?? 0),
          0,
        );

        amountsCents = selectedPropertiesAndFolders.map(([propOrFolder]) =>
          propOrFolder
            ? Math.trunc(
                ((propOrFolder.surface_total ?? 0) / totalSurface) *
                  totalAmountCents,
              )
            : null,
        );
      } else if (repartitionType === RepartitionType.UNIFORM) {
        const propertiesAndFoldersCount = selectedPropertiesAndFolders.filter(
          ([propOrFolder]) => !!propOrFolder,
        ).length;
        amountsCents = selectedPropertiesAndFolders.map(([propOrFolder]) =>
          propOrFolder
            ? Math.trunc(totalAmountCents / propertiesAndFoldersCount)
            : null,
        );
      } else if (
        repartitionType === RepartitionType.MANUAL &&
        selectedRepartitionKey
      ) {
        const base = repartitionKeyBase(selectedRepartitionKey);
        amountsCents = selectedRepartitionKey.repartition.map((r) =>
          base > 0 ? Math.trunc((totalAmountCents * r.montant) / base) : 0,
        );
        if (selectedRepartitionKey.unit === RepartitionKeyUnit.SHARE) {
          expectedTotalCents =
            base > 0
              ? Math.round(
                  (totalAmountCents *
                    repartitionKeyAllocatedShares(selectedRepartitionKey)) /
                    base,
                )
              : 0;
        }
      }

      if (
        repartitionType !== RepartitionType.MANUAL ||
        selectedRepartitionKey
      ) {
        let cumulAmount = amountsCents.reduce<number>(
          (acc, amount) => acc + (amount ?? 0),
          0,
        );
        // Ensure the sum of amounts is equal to the total amount and distribute potential rounding error on amounts
        if (cumulAmount !== expectedTotalCents) {
          const sign = expectedTotalCents - cumulAmount > 0 ? 1 : -1;
          let i = 0;
          while (
            expectedTotalCents !== cumulAmount &&
            i < amountsCents.length
          ) {
            amountsCents[i] = (amountsCents[i] ?? 0) + sign;
            i++;
            cumulAmount += sign;
          }
        }
      }
      await Promise.all(
        amountsCents.map((amountCents, i) =>
          setFieldValue(
            `${name}.${i}.montant`,
            amountCents ? amountCents / 100 : "",
          ),
        ),
      );
    })();
  }, [
    balanceAffectationsCSV,
    childBalances,
    errorFolders,
    errorProperties,
    isLoadingFolders,
    isLoadingProperties,
    name,
    parentFolder,
    parentId,
    properties,
    propertiesById,
    propertyFolders,
    propertyFoldersById,
    repartitionType,
    selectedRepartitionKey,
    setFieldValue,
    totalAmountCents,
    values.id_transaction,
  ]);

  const allocatedShares = isShare
    ? getIn(values, name, []).reduce(
        (acc: number, balance: Balance) =>
          acc + (parseFloat(String(balance.montant)) || 0),
        0,
      )
    : 0;

  useEffect(() => {
    const totalSplitAmount = Math.round(
      getIn(values, name, []).reduce(
        (acc: number, balance: Balance) =>
          acc + (parseFloat(String(balance.montant)) || 0) * 100,
        0,
      ),
    );

    if (isShare) {
      setRepartitionWarning(
        totalShares > 0 && allocatedShares > totalShares
          ? `La somme des tantièmes des lots (${allocatedShares}) dépasse le total de la clé (${totalShares}).`
          : "",
      );
    } else if (
      selectedRepartitionKey?.unit === RepartitionKeyUnit.SHARE &&
      repartitionKeyAllocatedShares(selectedRepartitionKey) <
        repartitionKeyBase(selectedRepartitionKey)
    ) {
      // Clé partielle : le reste du montant revient aux lots sortis de la clé, c'est voulu.
      setRepartitionWarning("");
    } else if (totalSplitAmount !== totalAmountCents) {
      if (isFinanceConfig) {
        setRepartitionWarning(
          `La somme des pourcentages est égale à ${totalSplitAmount / 100}%`,
        );
      } else {
        setRepartitionWarning(
          `La somme des montant répartis (${currencyFormatter(
            totalSplitAmount / 100,
          )}) ne correspond pas au montant total (${currencyFormatter(
            totalAmountCents / 100,
          )}).`,
        );
      }
    } else {
      setRepartitionWarning("");
    }
  }, [
    allocatedShares,
    isFinanceConfig,
    isShare,
    name,
    selectedRepartitionKey,
    totalAmountCents,
    totalShares,
    values,
  ]);

  // Changer d'unité en cours de saisie vide les montants : des pourcentages déjà
  // calculés n'ont aucun sens lus comme des tantièmes, et inversement.
  const prevIsShare = useRef(isShare);
  useEffect(() => {
    if (prevIsShare.current === isShare) return;
    prevIsShare.current = isShare;
    getIn(values, name, []).forEach((_balance: Balance, i: number) => {
      setFieldValue(`${name}.${i}.montant`, "");
    });
  }, [isShare, name, setFieldValue, values]);

  const repartitionTypeOptions = hasPeriod
    ? [...REPARTITION_TYPE_OPTIONS, METER_REPARTITION_TYPE_OPTION]
    : REPARTITION_TYPE_OPTIONS;

  return (
    <div className={styles.wrapper}>
      <Text variant="deci" weight="bold">
        Répartition
      </Text>
      <FieldArray name={name}>
        {({ push, remove }) => {
          return (
            <>
              {!isShare && (
                <ReactSelectStandalone
                  label="Type de répartition"
                  options={repartitionTypeOptions}
                  value={repartitionTypeOptions.find(
                    (o) => o.value === repartitionType,
                  )}
                  onChange={(
                    option: { value: string; label: string } | null,
                  ) => {
                    setSelectedRepartitionKey(null);
                    setRepartitionType(
                      option?.value ?? RepartitionType.PRORATA_SURFACE,
                    );
                  }}
                  isClearable={false}
                />
              )}
              {!isShare && domaineBalance === BalanceDomain.FOLDER && (
                <div>
                  {Array.isArray(repartitionKeys) &&
                  repartitionKeys.length > 0 ? (
                    <ReactSelectStandalone
                      options={repartitionKeys
                        .filter((key: RepartitionKey) => key && key.id)
                        .map((key: RepartitionKey) => ({
                          value: key.id,
                          label: key.nom,
                        }))}
                      placeholder="Sélectionner une clé de répartition"
                      value={
                        selectedRepartitionKey
                          ? {
                              value: selectedRepartitionKey.id,
                              label: selectedRepartitionKey.nom,
                            }
                          : null
                      }
                      onChange={async (
                        option: { value: number; label: string } | null,
                      ) => {
                        if (!option) {
                          setSelectedRepartitionKey(null);
                          return;
                        }
                        const selectedKey = repartitionKeys.find(
                          (k: RepartitionKey) => k.id === option.value,
                        );
                        if (selectedKey) {
                          setSelectedRepartitionKey(selectedKey);
                          setRepartitionType(RepartitionType.MANUAL);
                          const repartitions = selectedKey.repartition.map(
                            (r: RepartitionKeyEntry) => {
                              return {
                                ...balanceIV,
                                ...r,
                                affectation: formatBalanceAffectation(
                                  r.domaine,
                                  getBalanceParentId(r),
                                ),
                                montant: "",
                                correlation_id: correlationId.current,
                              };
                            },
                          );
                          await setFieldValue(name, repartitions);
                        }
                      }}
                      isClearable
                      label="Clé de répartition"
                    />
                  ) : (
                    <Text variant="deci">
                      Vous pouvez définir un template de clé de répartition ici
                      :{" "}
                      <Link
                        to={`/bailleur/properties/folder/${parentId}?tab-prop-show=parametres`}
                      >
                        Créer une clé de répartition
                      </Link>
                    </Text>
                  )}
                </div>
              )}
              {repartitionType === RepartitionType.METER && (
                <MeterSplitPanel
                  parentId={parentId}
                  periodeDebut={periodeDebut ?? ""}
                  periodeFin={periodeFin ?? ""}
                  montantTotal={totalAmountCents / 100}
                  onSplitCalculated={(splits) => {
                    const newBalances = splits.map((split) => ({
                      ...balanceIV,
                      id_bien: split.property_id,
                      domaine: BalanceDomain.PROPERTY,
                      affectation: formatBalanceAffectation(
                        BalanceDomain.PROPERTY,
                        split.property_id,
                      ),
                      montant: split.amount,
                      correlation_id: correlationId.current,
                    }));
                    setFieldValue(name, newBalances);
                  }}
                />
              )}
              {repartitionType !== RepartitionType.METER && (
                <>
                  <div className={styles.list}>
                    {getIn(values, name, []).map(
                      (
                        _balanceValues: Balance,
                        index: number,
                        array: Balance[],
                      ) => {
                        const prefix = `${name}.${index}.`;

                        return (
                          <div className={styles.row} key={index}>
                            <AffectationSelect
                              prefix={prefix}
                              parentId={parentId}
                              domaineBalance={domaineBalance}
                              disabledOptions={balanceAffectations}
                              mandatory
                              label=""
                            />
                            <TextInput
                              type="number"
                              step="0.01"
                              name={`${prefix}montant`}
                              placeholder={isShare ? "350" : "80"}
                              size={"md"}
                              mandatory
                              min={null}
                              unit={
                                isShare
                                  ? undefined
                                  : isFinanceConfig
                                  ? "%"
                                  : "€"
                              }
                              marginForm={""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setRepartitionType(RepartitionType.MANUAL);
                                setSelectedRepartitionKey(null);
                                handleChange(e);
                              }}
                              className={styles.amountInput}
                            />
                            {array.length > 1 && (
                              <IconButton
                                type="button"
                                icon={IconTrash}
                                label="Supprimer cette répartition"
                                variant="plain"
                                size="sm"
                                onClick={() => {
                                  setSelectedRepartitionKey(null);
                                  remove(index);
                                }}
                              />
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                  {balanceAffectations.length !== optionsNb && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() =>
                        push({
                          ...balanceIV,
                          correlation_id: correlationId.current,
                        })
                      }
                      icon={IconPlus}
                    >
                      Ajouter un bien
                    </Button>
                  )}
                </>
              )}
              {isShare && (
                <Text color="muted">
                  Tantièmes affectés : {allocatedShares}
                  {totalShares > 0 ? ` / ${totalShares}` : ""}
                </Text>
              )}
              <Callout variant="warning">{repartitionWarning}</Callout>
            </>
          );
        }}
      </FieldArray>
    </div>
  );
}
